import { FILE_TYPES_META, FileType } from '@models/file';

import { resolveGraphqlBaseUrl } from './tenant-utils';
import { getUserToken } from './userUtils';

const KNOWN_FILE_EXTENSIONS = new Set([
  'pdf',
  'docx',
  'doc',
  'jpeg',
  'jpg',
  'png',
  'xls',
  'xlsx',
  'csv'
]);

const EXTENSION_NAME_HINTS = [
  'xlsx',
  'docx',
  'jpeg',
  'xls',
  'csv',
  'pdf',
  'doc',
  'png',
  'jpg'
] as const;

export function getExtensionFromFileName(fileName: string): string | undefined {
  const lastDot = fileName.lastIndexOf('.');

  if (lastDot <= 0 || lastDot === fileName.length - 1) {
    return undefined;
  }

  const extension = fileName.slice(lastDot + 1).toLowerCase();

  return KNOWN_FILE_EXTENSIONS.has(extension) ? extension : undefined;
}

export function inferExtensionFromDocumentName(
  documentName: string
): string | undefined {
  if (!documentName) {
    return undefined;
  }

  const fromFileName = getExtensionFromFileName(documentName);

  if (fromFileName) {
    return fromFileName;
  }

  const lower = documentName.toLowerCase();

  for (const ext of EXTENSION_NAME_HINTS) {
    const regex = new RegExp(`(?:^|[-_.])${ext}(?:$|[-_.])`, 'i');

    if (regex.test(lower)) {
      return ext;
    }
  }

  return undefined;
}

export function resolveFileExtension(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const fromFileName = getExtensionFromFileName(candidate);

    if (fromFileName) {
      return fromFileName;
    }

    const inferred = inferExtensionFromDocumentName(candidate);

    if (inferred) {
      return inferred;
    }
  }

  return undefined;
}

export function appendExtensionIfMissing(
  baseName: string,
  extension?: string
): string {
  if (!extension || getExtensionFromFileName(baseName)) {
    return baseName;
  }

  return `${baseName}.${extension}`;
}

export function getExtensionFromContentType(
  contentType?: string | null
): string | undefined {
  if (!contentType) {
    return undefined;
  }

  const match = Object.entries(FILE_TYPES_META).find(
    ([, meta]) => meta.mimetype === contentType
  );

  return match?.[0];
}

export function resolveDocumentFileName(
  documentName: string,
  fileName?: string | null,
  documentPath?: string | null,
  contentType?: string | null,
  storedExtension?: string | null
): string {
  if (fileName && getExtensionFromFileName(fileName)) {
    return fileName;
  }

  const pathBaseName = documentPath?.split(/[/\\]/).pop();
  const displayName = documentName || fileName || pathBaseName || 'Document';

  const extension =
    resolveFileExtension(
      fileName,
      pathBaseName,
      documentName,
      storedExtension
    ) || getExtensionFromContentType(contentType);

  return appendExtensionIfMissing(displayName, extension);
}

export function resolveDownloadFileName(
  title: string,
  fileName: string,
  contentType?: string
): string {
  if (getExtensionFromFileName(fileName)) {
    const extension = fileName.split('.').pop();

    return title ? `${title}.${extension}` : fileName;
  }

  const extension =
    resolveFileExtension(fileName, title) ||
    getExtensionFromContentType(contentType);

  return appendExtensionIfMissing(title || fileName, extension);
}

export function getFileIconPathForExtension(
  extension?: string | null
): string {
  switch (extension) {
    case 'pdf':
      return '/icons/pdf-icon.svg';
    case 'docx':
    case 'doc':
      return '/icons/doc-icon.png';
    case 'jpeg':
    case 'jpg':
      return '/icons/jpg-icon.png';
    case 'png':
      return '/icons/png-icon.png';
    case 'xls':
    case 'xlsx':
    case 'csv':
      return '/icons/excel-icon.svg';
    default:
      return '/icons/file-summary-primary.svg';
  }
}

export function getFileIconPath(
  fileName: string,
  contentType?: string,
  ...extraCandidates: Array<string | null | undefined>
): string {
  const extension =
    resolveFileExtension(fileName, ...extraCandidates) ||
    getExtensionFromContentType(contentType);

  return getFileIconPathForExtension(extension);
}

function parseContentDispositionFilename(
  header: string | null
): string | undefined {
  if (!header) {
    return undefined;
  }

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);

  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const match = header.match(/filename="?([^";]+)"?/i);

  return match?.[1];
}

interface DownloadByPathPayload {
  path: string;
  documentName: string;
}

export function isFile(input: any) {
  if ('File' in window && input instanceof File) return true;
  else return false;
}

export function deriveMimeTypes(fileTypes?: FileType[]): string {
  if (fileTypes) {
    const extMimeTypes = fileTypes.map(
      (type) => FILE_TYPES_META[type].mimetype
    );

    return extMimeTypes.join(', ');
  }

  return '';
}

export function generateDownloadLink(
  fileId: string,
  entityTypeId: string,
  entityId: string,
  entityViewId: string,
  propertyId: string
): string {
  return `${resolveGraphqlBaseUrl()}/api/Files/download/entities?entityTypeId=${entityTypeId}&entityId=${entityId}&entityViewId=${entityViewId}&propertyId=${propertyId}&fileId=${fileId}`;
}
export function generateNewDownloadLink(documentId: string): string {
  return `${resolveGraphqlBaseUrl()}/api/Documents/download/${documentId}`;
}

export async function downloadFileFromUrl(url: string, fileName: string) {
  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${getUserToken()}`
      }
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch file from ${url}`);
    }

    const blob = await response.blob();
    const serverFileName = parseContentDispositionFilename(
      response.headers.get('Content-Disposition')
    );
    const extensionFromBlob = getExtensionFromContentType(blob.type);
    const resolvedFileName =
      serverFileName ||
      appendExtensionIfMissing(
        resolveDownloadFileName(
          fileName.split('.')[0],
          fileName,
          blob.type
        ),
        extensionFromBlob
      );

    // Create a link element
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = resolvedFileName || fileName;

    // Append the link to the body (required for Firefox)
    document.body.appendChild(link);

    // Programmatically click the link to trigger the download
    link.click();

    // Remove the link from the document
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading the file:', error);
  }
}

export async function downloadFileByPath(
  payload: DownloadByPathPayload
): Promise<void> {
  const url = `${resolveGraphqlBaseUrl()}/api/Documents/download/path`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getUserToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const blob = await response.blob();

    const link = document.createElement('a');
    const objectUrl = window.URL.createObjectURL(blob);

    link.href = objectUrl;
    link.download = payload.documentName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.error('Error downloading file by path:', error);
    throw error;
  }
}
